// Duplexer-vs-SSE reconciliation — pure helpers shared by the chat state
// (use-chat). Every SCHEDULER assistant row is delivered twice with the SAME
// persisted id: once live over SSE (`messageEnd` stamps it as `finalId`) and
// again via the duplexer republish (`entity.id`). The client keeps ONE bubble
// by matching those ids; these helpers decide whether an incoming duplexer
// entity is already on screen so it is never painted a second time.

type DedupMessage = {
  id?: string;
  finalId?: string;
  role?: string;
  content?: string;
  animate?: boolean;
};

type DedupEntity = { id?: string; role?: string; content?: string };

/** True when an entity's persisted id is already a row's id or its streamed `finalId`. */
export function isEntityShownById(messages: DedupMessage[], entityId: string): boolean {
  return messages.some((m) => m.id === entityId || m.finalId === entityId);
}

/**
 * True when a live-streamed bubble already shows this assistant text, so the
 * duplexer republish of the same row must not append a second identical bubble.
 *
 * The id match ({@link isEntityShownById}) is the happy path: `messageEnd`
 * stamps `finalId` and the twin dedupes cleanly. This content guard is the
 * safety net for when that link is lost — a truncated / raced SSE where
 * `messageEnd` never landed, so the streamed bubble has NO `finalId` and the id
 * check misses. Without it the duplexer twin appends as a byte-identical
 * duplicate the post-turn refetch can no longer collapse (our list is now
 * longer than the server's, so it declines to replace).
 *
 * Scoped to streamed bubbles — those carrying a persisted `finalId` OR still
 * flagged `animate` (born from `messageStart` this session, i.e. the
 * finalId-lost case). History-loaded rows carry neither, so a genuinely
 * repeated line from an earlier turn is never dropped.
 */
export function isStreamedDuplicate(messages: DedupMessage[], entity: DedupEntity): boolean {
  if (!entity.content) return false;
  return messages.some(
    (m) =>
      (!!m.finalId || m.animate === true) &&
      m.role === entity.role &&
      m.content === entity.content,
  );
}
